import { useCallback, useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '../cn.js';
import { Button } from './Button.js';

/**
 * A modal dialog with a proper focus trap.
 *
 * Approvals are granted in one of these, so the dialog has to be completely
 * operable by keyboard and must not let focus escape to the page behind it.
 * Built on the native <dialog> semantics manually rather than showModal(),
 * because showModal's ::backdrop cannot be styled with our token palette
 * consistently across browsers.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Blocks dismissal by Escape and backdrop click. For destructive confirmations. */
  dismissable?: boolean;
}

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-4xl',
  xl: 'max-w-5xl',
} as const;

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissable = true,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Return focus to whatever opened the dialog, so keyboard users are not
  // dumped back at the top of the document.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panelRef.current)?.focus();
    return () => previouslyFocused.current?.focus?.();
  }, [open]);

  // Prevent the page behind from scrolling while the dialog is up.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape' && dismissable) {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      // Focus trap: wrap from last to first and back again.
      const nodes = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
      ).filter((el) => el.offsetParent !== null);
      if (nodes.length === 0) return;

      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [dismissable, onClose],
  );

  if (!open) return null;

  return createPortal(
    // The keydown handler implements Escape-to-close and the focus trap for
    // everything inside the dialog. It is attached to the container rather
    // than the panel so a key pressed while focus sits on the backdrop is
    // still handled; the container is a positioning wrapper, not a control.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      onKeyDown={onKeyDown}
    >
      <div
        className="absolute inset-0 bg-overlay/70"
        onClick={dismissable ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        className={cn(
          'relative w-full bg-surface border border-line rounded-xl shadow-modal',
          'max-h-[90vh] flex flex-col animate-slide-up focus:outline-none',
          SIZES[size],
        )}
      >
        <header className="flex items-start justify-between gap-4 px-5 py-4 border-b border-line shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-body">{title}</h2>
            {description ? <p className="text-sm text-muted mt-1">{description}</p> : null}
          </div>
          {dismissable ? (
            <Button
              variant="ghost"
              size="xs"
              iconOnly
              aria-label="Close dialog"
              onClick={onClose}
              leadingIcon={<XMarkIcon className="w-4 h-4" aria-hidden="true" />}
            />
          ) : null}
        </header>

        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">{children}</div>

        {footer ? (
          <footer className="flex items-center justify-end gap-2 px-5 py-4 border-t border-line shrink-0">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
